import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';
import {
    FileText,
    Download,
    RotateCcw,
    Eye,
    X,
    RefreshCw,
    Package,
    Users,
    Printer,
    IndianRupee,
    History,
    Scale
} from 'lucide-react';
import { toast } from 'react-toastify';

// Import SPPO slice actions and selectors
import {
    fetchCCForSPPOCloseByVendor,
    fetchDCAForPOReport,
    fetchVendorsForSPPOReport,
    fetchSPPOReportData,
    fetchSPPOForPrint,
    setFilters,
    clearFilters,
    resetReportData,
    resetDependentDropdowns,
    resetVendorDropdown,
    clearError,
    selectSPPOCostCenters,
    selectSPPODCACodes,
    selectSPPOVendors,
    selectSPPOReportData,
    selectSPPOPrintData,
    selectLoading,
    selectErrors,
    selectIsAnyLoading,
    selectSPPOReportSummary,
} from '../../slices/spPOSlice/spPOReportSlice';

// Import budget slice for financial years
import {
    fetchAllFinancialYears,
    selectAllFinancialYears
} from '../../slices/budgetSlice/budgetReportSlice';
import { convertAmountToWords } from '../../utilities/amountToTextHelper';

// Utility functions
const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '0.00';
    return new Intl.NumberFormat('en-IN', {
        style: 'decimal',
        minimumFractionDigits: 2
    }).format(amount);
};

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB');
    } catch {
        return dateString;
    }
};

// Tooltip Component
const Tooltip = ({ children, content }) => {
    const [showTooltip, setShowTooltip] = useState(false);

    return (
        <div 
            className="relative inline-block"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            {children}
            {showTooltip && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 text-xs text-white bg-gray-900 dark:bg-gray-700 rounded-lg shadow-lg whitespace-nowrap z-50">
                    {content}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                </div>
            )}
        </div>
    );
};

// Modal Component
const Modal = ({ isOpen, onClose, title, children, size = 'xl' }) => {
    if (!isOpen) return null;

    const sizeClasses = {
        sm: 'max-w-md',
        md: 'max-w-lg',
        lg: 'max-w-4xl',
        xl: 'max-w-6xl',
        full: 'max-w-7xl'
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>
                
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
                
                <div className={clsx(
                    "inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-full",
                    sizeClasses[size]
                )}>
                    <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                {title}
                            </h3>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                            {children}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// SPPO Print Component
// Styles are scoped under .sppo-doc and rendered inside the printed markup, so the preview matches the printout
const SPPO_PRINT_CSS = `
    .sppo-doc { font-family: 'Segoe UI', Arial, sans-serif; color: #1f2937; background: #fff; max-width: 210mm; margin: 0 auto; padding: 10mm 12mm; font-size: 12px; line-height: 1.45; }
    .sppo-doc * { box-sizing: border-box; }
    .sppo-doc .doc-header { display: flex; align-items: center; gap: 14px; padding-bottom: 10px; border-bottom: 3px solid #1b365d; }
    .sppo-doc .doc-logo { width: 64px; height: 64px; object-fit: cover; border-radius: 8px; flex-shrink: 0; }
    .sppo-doc .doc-company { flex: 1; }
    .sppo-doc .doc-company-name { font-size: 20px; font-weight: 800; color: #1b365d; letter-spacing: 0.5px; }
    .sppo-doc .doc-company-tagline { font-size: 11px; font-style: italic; color: #e87722; margin: 1px 0 4px; }
    .sppo-doc .doc-company-address { font-size: 11px; color: #4b5563; }
    .sppo-doc .doc-title-block { text-align: right; flex-shrink: 0; }
    .sppo-doc .doc-title { font-size: 20px; font-weight: 800; color: #1b365d; letter-spacing: 1px; }
    .sppo-doc .doc-subtitle { font-size: 11px; font-weight: 600; color: #e87722; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 6px; }
    .sppo-doc .doc-ref { border-collapse: collapse; margin-left: auto; font-size: 11px; }
    .sppo-doc .doc-ref td { border: 1px solid #cbd5e1; padding: 3px 8px; }
    .sppo-doc .doc-ref td:first-child { background: #f1f5f9; font-weight: 600; color: #475569; }
    .sppo-doc .doc-ref td:last-child { font-weight: 700; color: #111827; }
    .sppo-doc .doc-accent { height: 3px; background: #e87722; margin-top: 2px; }
    .sppo-doc .doc-parties { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 14px 0; }
    .sppo-doc .doc-box { border: 1px solid #cbd5e1; }
    .sppo-doc .doc-box-title { background: #1b365d; color: #fff; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; padding: 5px 10px; }
    .sppo-doc .doc-box-body { padding: 8px 10px; }
    .sppo-doc .doc-vendor-name { font-size: 14px; font-weight: 700; color: #111827; margin-bottom: 3px; }
    .sppo-doc .doc-field { display: flex; gap: 6px; margin-bottom: 3px; }
    .sppo-doc .doc-field-label { color: #64748b; min-width: 90px; flex-shrink: 0; }
    .sppo-doc .doc-field-value { color: #111827; font-weight: 600; }
    .sppo-doc .doc-section-title { font-size: 12px; font-weight: 700; color: #1b365d; text-transform: uppercase; letter-spacing: 0.8px; margin: 16px 0 6px; padding-left: 8px; border-left: 3px solid #e87722; }
    .sppo-doc .doc-table { width: 100%; border-collapse: collapse; font-size: 11.5px; }
    .sppo-doc .doc-table th { background: #1b365d; color: #fff; font-weight: 600; padding: 6px 8px; border: 1px solid #1b365d; text-align: left; }
    .sppo-doc .doc-table td { padding: 6px 8px; border: 1px solid #cbd5e1; vertical-align: top; }
    .sppo-doc .doc-table tbody tr:nth-child(even) td { background: #f8fafc; }
    .sppo-doc .doc-table thead { display: table-header-group; }
    .sppo-doc .doc-table tr { page-break-inside: avoid; }
    .sppo-doc .text-right { text-align: right !important; }
    .sppo-doc .text-center { text-align: center !important; }
    .sppo-doc .doc-total td { background: #f1f5f9 !important; font-weight: 700; color: #111827; font-size: 12.5px; }
    .sppo-doc .doc-words { border: 1px solid #cbd5e1; border-top: none; padding: 6px 8px; font-size: 11.5px; }
    .sppo-doc .doc-words span { color: #64748b; }
    .sppo-doc .doc-terms { margin: 0; padding-left: 18px; font-size: 11.5px; color: #374151; }
    .sppo-doc .doc-terms li { margin-bottom: 3px; }
    .sppo-doc .doc-signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 40px; page-break-inside: avoid; }
    .sppo-doc .doc-sign { font-size: 11.5px; }
    .sppo-doc .doc-sign-for { font-weight: 700; color: #1b365d; }
    .sppo-doc .doc-sign-space { height: 50px; border-bottom: 1px solid #475569; }
    .sppo-doc .doc-sign-label { color: #64748b; margin-top: 4px; }
    .sppo-doc .doc-sign.right { text-align: right; }
    .sppo-doc .doc-footer { margin-top: 24px; padding-top: 6px; border-top: 1px solid #cbd5e1; text-align: center; font-size: 10px; color: #94a3b8; }
`;

const SPPOPrint = ({ sppoData, onClose }) => {
    const printRef = useRef();

    const handlePrint = () => {
        const printContent = printRef.current;
        const winPrint = window.open('', '', 'left=0,top=0,width=900,height=700,toolbar=0,scrollbars=0,status=0');

        winPrint.document.write(`
            <html>
                <head>
                    <title>SPPO - ${sppoData?.InitialPOData?.SPPONo}</title>
                    <style>
                        @page { size: A4; margin: 0; }
                        body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    </style>
                </head>
                <body>
                    ${printContent.innerHTML}
                </body>
            </html>
        `);

        winPrint.document.close();
        // Wait for the logo to load, otherwise it prints blank
        const images = Array.from(winPrint.document.images);
        Promise.all(images.map(img => img.complete ? Promise.resolve() : new Promise(resolve => { img.onload = img.onerror = resolve; })))
            .then(() => {
                winPrint.focus();
                winPrint.print();
                winPrint.close();
            });
    };

    const initialData = sppoData?.InitialPOData || {};
    const amendData = sppoData?.SPPOAmendData || [];
    const items = sppoData?.POItems || [];

    // Parse terms and conditions
    const parseTermsAndConditions = (remarks) => {
        if (!remarks) return [];
        return remarks.split('|').filter(term => term.trim()).map(term => term.trim());
    };

    const terms = parseTermsAndConditions(initialData.Remarks);

    return (
        <Modal isOpen={true} onClose={onClose} title="SPPO Print Preview" size="full">
            <div className="flex justify-end mb-4 space-x-2">
                <button
                    onClick={handlePrint}
                    className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 flex items-center gap-2 shadow-lg transition-all duration-300"
                >
                    <Printer className="h-5 w-5" />
                    Print SPPO
                </button>
                <button
                    onClick={onClose}
                    className="px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 shadow-lg transition-all duration-300"
                >
                    Close Preview
                </button>
            </div>

            <div ref={printRef} className="bg-white">
                <style>{SPPO_PRINT_CSS}</style>
                <div className="sppo-doc">
                    {/* Letterhead */}
                    <div className="doc-header">
                        <img className="doc-logo" src="/sllogo.png" alt="Company Logo" />
                        <div className="doc-company">
                            <div className="doc-company-name">ESSEL PROJECTS PVT LTD</div>
                            <div className="doc-company-tagline">Built On Integrity. Driven By Performance</div>
                            <div className="doc-company-address">Corporate Office: 522, 5th Floor, Chaitanya Business Park, Devpuri, Raipur - 492015</div>
                        </div>
                        <div className="doc-title-block">
                            <div className="doc-title">PURCHASE ORDER</div>
                            <div className="doc-subtitle">Service Provider</div>
                            <table className="doc-ref">
                                <tbody>
                                    <tr><td>PO No.</td><td>{initialData.SPPONo || 'N/A'}</td></tr>
                                    <tr><td>PO Date</td><td>{initialData.SPPOStartDate || 'N/A'}</td></tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="doc-accent"></div>

                    {/* Vendor and Order Details */}
                    <div className="doc-parties">
                        <div className="doc-box">
                            <div className="doc-box-title">To (Service Provider)</div>
                            <div className="doc-box-body">
                                <div className="doc-vendor-name">{initialData.VendorName || 'N/A'}</div>
                                <div className="doc-field"><span className="doc-field-label">Vendor Code</span><span className="doc-field-value">{initialData.VendorCode || 'N/A'}</span></div>
                                <div className="doc-field"><span className="doc-field-label">Address</span><span className="doc-field-value">{initialData.VendorAddress || 'N/A'}</span></div>
                                <div className="doc-field"><span className="doc-field-label">Phone</span><span className="doc-field-value">{initialData.VendorPhoneNo || 'N/A'}</span></div>
                            </div>
                        </div>
                        <div className="doc-box">
                            <div className="doc-box-title">Order Details</div>
                            <div className="doc-box-body">
                                <div className="doc-field"><span className="doc-field-label">Start Date</span><span className="doc-field-value">{initialData.SPPOStartDate || 'N/A'}</span></div>
                                <div className="doc-field"><span className="doc-field-label">End Date</span><span className="doc-field-value">{initialData.SPPOEndDate || 'N/A'}</span></div>
                                <div className="doc-field"><span className="doc-field-label">Cost Center</span><span className="doc-field-value">{initialData.CCCode || 'N/A'}</span></div>
                                <div className="doc-field"><span className="doc-field-label">DCA Code</span><span className="doc-field-value">{initialData.DCACode || 'N/A'}</span></div>
                                <div className="doc-field"><span className="doc-field-label">Sub DCA</span><span className="doc-field-value">{initialData.SubDcaCode || 'N/A'}</span></div>
                            </div>
                        </div>
                    </div>

                    {/* Items */}
                    <div className="doc-section-title">Scope of Work / Items</div>
                    <table className="doc-table">
                        <thead>
                            <tr>
                                <th className="text-center" style={{ width: '6%' }}>S.No</th>
                                <th>Description</th>
                                <th className="text-center" style={{ width: '9%' }}>Unit</th>
                                <th className="text-right" style={{ width: '10%' }}>Qty</th>
                                <th className="text-right" style={{ width: '14%' }}>Rate (₹)</th>
                                <th className="text-right" style={{ width: '16%' }}>Amount (₹)</th>
                                <th className="text-center" style={{ width: '10%' }}>Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length > 0 ? items.map((item, index) => (
                                <tr key={index}>
                                    <td className="text-center">{index + 1}</td>
                                    <td>{item.Description || '-'}</td>
                                    <td className="text-center">{item.Unit || '-'}</td>
                                    <td className="text-right">{item.Quantity || 0}</td>
                                    <td className="text-right">{formatCurrency(item.Rate || 0)}</td>
                                    <td className="text-right">{formatCurrency(item.Amount || 0)}</td>
                                    <td className="text-center">{item.Type || '-'}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="7" className="text-center" style={{ padding: '24px', color: '#6b7280' }}>
                                        No items found for this SPPO
                                    </td>
                                </tr>
                            )}
                            <tr className="doc-total">
                                <td colSpan="5" className="text-right">Total Order Value (₹)</td>
                                <td className="text-right">{formatCurrency(initialData.TotalValue || 0)}</td>
                                <td></td>
                            </tr>
                        </tbody>
                    </table>
                    <div className="doc-words">
                        <span>Amount in words: </span><strong>{convertAmountToWords(initialData.TotalValue || 0)}</strong>
                    </div>

                    {/* Amendment History */}
                    {amendData.length > 0 && (
                        <>
                            <div className="doc-section-title">Amendments</div>
                            <table className="doc-table">
                                <thead>
                                    <tr>
                                        <th className="text-center" style={{ width: '6%' }}>S.No</th>
                                        <th style={{ width: '18%' }}>Amended Date</th>
                                        <th className="text-right" style={{ width: '18%' }}>Added Amount (₹)</th>
                                        <th>Remarks</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {amendData.map((amend, index) => (
                                        <tr key={index}>
                                            <td className="text-center">{index + 1}</td>
                                            <td>{amend.AmendedDate || '-'}</td>
                                            <td className="text-right">{formatCurrency(amend.AddAmount || 0)}</td>
                                            <td>{amend.Remarks || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}

                    {/* Terms and Conditions */}
                    {terms.length > 0 && (
                        <>
                            <div className="doc-section-title">Terms & Conditions</div>
                            <ol className="doc-terms">
                                {terms.map((term, index) => (
                                    <li key={index}>{term}</li>
                                ))}
                            </ol>
                        </>
                    )}

                    {/* Signatures */}
                    <div className="doc-signatures">
                        <div className="doc-sign">
                            <div className="doc-sign-for">Accepted by Service Provider</div>
                            <div className="doc-sign-space"></div>
                            <div className="doc-sign-label">Signature, Name & Seal</div>
                        </div>
                        <div className="doc-sign right">
                            <div className="doc-sign-for">For ESSEL PROJECTS PVT LTD</div>
                            <div className="doc-sign-space"></div>
                            <div className="doc-sign-label">Authorised Signatory</div>
                        </div>
                    </div>

                    <div className="doc-footer">
                        This is a system-generated Purchase Order. Please quote the PO number on all invoices and correspondence.
                    </div>
                </div>
            </div>
        </Modal>
    );
};

// SPPO Details Modal Component
const SPPODetailsModal = ({ isOpen, onClose, sppoData, loading }) => {
    const [showPrintModal, setShowPrintModal] = useState(false);

    const initialData = sppoData?.Data?.InitialPOData || sppoData?.InitialPOData || {};
    const amendData = sppoData?.Data?.SPPOAmendData || sppoData?.SPPOAmendData || [];
    const items = sppoData?.Data?.POItems || sppoData?.POItems || [];

    // Parse terms and conditions
    const parseTermsAndConditions = (remarks) => {
        if (!remarks) return [];
        return remarks.split('|').filter(term => term.trim()).map(term => term.trim());
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} title="SPPO Details" size="full">
                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <RotateCcw className="h-6 w-6 text-indigo-500 animate-spin mr-3" />
                        <p className="text-indigo-700 dark:text-indigo-300">Loading SPPO details...</p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-2 mb-4">
                            <button
                                onClick={() => setShowPrintModal(true)}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                            >
                                <Printer className="h-4 w-4" />
                                Print SPPO
                            </button>
                        </div>

                        {/* SPPO Header Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                            <div className="bg-gray-50 dark:bg-gray-700 dark:text-indigo-100 p-4 rounded-lg">
                                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Basic Information</h4>
                                <p><strong>SPPO No:</strong> {initialData.SPPONo || '-'}</p>
                                <p><strong>Start Date:</strong> {initialData.SPPOStartDate || '-'}</p>
                                <p><strong>End Date:</strong> {initialData.SPPOEndDate || '-'}</p>
                                <p><strong>Status:</strong> {initialData.POStatus || '-'}</p>
                            </div>
                            
                            <div className="bg-gray-50 dark:bg-gray-700 p-4 dark:text-indigo-100 rounded-lg">
                                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Vendor Information</h4>
                                <p><strong>Vendor:</strong> {initialData.VendorName || '-'}</p>
                                <p><strong>Code:</strong> {initialData.VendorCode || '-'}</p>
                                <p><strong>Phone:</strong> {initialData.VendorPhoneNo || '-'}</p>
                                <p><strong>Address:</strong> {initialData.VendorAddress || '-'}</p>
                            </div>
                            
                            <div className="bg-gray-50 dark:bg-gray-700 p-4 dark:text-indigo-100 rounded-lg">
                                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Cost Center Details</h4>
                                <p><strong>CC Code:</strong> {initialData.CCCode || '-'}</p>
                                <p><strong>DCA Code:</strong> {initialData.DCACode || '-'}</p>
                                <p><strong>Sub DCA:</strong> {initialData.SubDcaCode || '-'}</p>
                                <p><strong>Total Value:</strong> ₹{formatCurrency(initialData.TotalValue || 0)}</p>
                                <p><strong>Balance:</strong> ₹{formatCurrency(initialData.Balance || 0)}</p>
                            </div>
                        </div>

                        {/* Amendment History */}
                        {amendData && amendData.length > 0 && (
                            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
                                <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                    <History className="h-5 w-5 text-amber-600" />
                                    Amendment History ({amendData.length} amendments)
                                </h4>
                                <div className="space-y-3">
                                    {amendData.map((amend, index) => (
                                        <div key={index} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                                                <div><strong>Date:</strong> {amend.AmendedDate}</div>
                                                <div><strong>Added Amount:</strong> ₹{formatCurrency(amend.AddAmount || 0)}</div>
                                                <div><strong>Subtract Amount:</strong> ₹{formatCurrency(amend.SubstractAmount || 0)}</div>
                                            </div>
                                            {amend.Remarks && (
                                                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                                    <strong>Remarks:</strong> {amend.Remarks}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Items Table */}
                        <div className="overflow-x-auto">
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Item Details</h4>
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gradient-to-r from-indigo-600 to-purple-700">
                                    <tr>
                                        <th className="px-4 py-3 text-xs font-bold text-white uppercase">S.No</th>
                                        <th className="px-4 py-3 text-xs font-bold text-white uppercase">Description</th>
                                        <th className="px-4 py-3 text-xs font-bold text-white uppercase">Unit</th>
                                        <th className="px-4 py-3 text-xs font-bold text-white uppercase text-right">Quantity</th>
                                        <th className="px-4 py-3 text-xs font-bold text-white uppercase text-right">Rate</th>
                                        <th className="px-4 py-3 text-xs font-bold text-white uppercase text-right">Amount</th>
                                        <th className="px-4 py-3 text-xs font-bold text-white uppercase">Type</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {items.length > 0 ? items.map((item, index) => (
                                        <tr key={index} className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${item.Type === 'Amend' ? 'bg-amber-50 dark:bg-amber-900/10' : ''}`}>
                                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{index + 1}</td>
                                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                <div className="max-w-md">
                                                    {item.Description || '-'}
                                                    {item.Type === 'Amend' && (
                                                        <span className="ml-2 px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded-full">
                                                            Amendment
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{item.Unit || '-'}</td>
                                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-right">{item.Quantity || 0}</td>
                                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-right font-medium">
                                                ₹{formatCurrency(item.Rate || 0)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-right font-bold">
                                                ₹{formatCurrency(item.Amount || 0)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                <span className={clsx(
                                                    "px-2 py-1 rounded-full text-xs font-medium",
                                                    item.Type === 'Direct' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                                    item.Type === 'Amend' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200' :
                                                    'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                                )}>
                                                    {item.Type || 'Direct'}
                                                </span>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="7" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                                No item details available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Terms & Conditions */}
                        {initialData.Remarks && (
                            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Terms & Conditions</h4>
                                <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                                    {parseTermsAndConditions(initialData.Remarks).map((term, index) => (
                                        <p key={index} className="flex items-start gap-2">
                                            <span className="text-indigo-600 dark:text-indigo-400 mt-1">•</span>
                                            <span>{term}</span>
                                        </p>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            {/* Print Modal */}
            {showPrintModal && (
                <SPPOPrint
                    sppoData={sppoData?.Data || sppoData}
                    onClose={() => setShowPrintModal(false)}
                />
            )}
        </>
    );
};

// Summary strip (compact)
const SummaryCards = ({ reportSummary }) => {
    const cards = [
        { title: 'Total SPPOs', value: reportSummary.totalSPPOs, icon: FileText, tone: 'text-indigo-600 dark:text-indigo-400', isCount: true },
        { title: 'Total Amount', value: reportSummary.totalAmount, icon: IndianRupee, tone: 'text-green-600 dark:text-green-400' },
        { title: 'Balance Order Value', value: reportSummary.totalBalance, icon: Scale, tone: 'text-orange-600 dark:text-orange-400' },
        { title: 'Vendors', value: reportSummary.uniqueVendors, icon: Users, tone: 'text-purple-600 dark:text-purple-400', isCount: true },
    ];

    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {cards.map((card) => (
                <div key={card.title} className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
                    <card.icon className={`h-5 w-5 flex-shrink-0 ${card.tone}`} />
                    <div className="min-w-0">
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">{card.title}</p>
                        <p className="text-sm font-bold text-gray-900 dark:text-white leading-tight truncate">
                            {card.isCount ? card.value : `₹${formatCurrency(card.value)}`}
                        </p>
                    </div>
                </div>
            ))}
        </div>
    );
};

// Helper function to download data as Excel
const downloadAsExcel = (data, filename) => {
    try {
        const csvContent = convertToCSV(data);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    } catch (error) {
        toast.error('Error downloading Excel file');
    }
};

const convertToCSV = (data) => {
    if (!Array.isArray(data) || data.length === 0) return '';

    const headers = Object.keys(data[0]);
    const csvContent = [
        headers.join(','),
        ...data.map(row =>
            headers.map(header => {
                const value = row[header];
                return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
            }).join(',')
        )
    ].join('\n');

    return csvContent;
};

// spGetSppoReport values: POStatus 'Open' is shown as "Running"; Year 'Any' = all financial years
const PO_STATUS_OPTIONS = [
    { value: 'SelectAll', label: 'Select All' },
    { value: 'Open', label: 'Running' },
    { value: 'Closed', label: 'Closed' },
];
const poStatusLabel = (s) => (s === 'Open' ? 'Running' : s || '-');

const MONTH_OPTIONS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
].map((label, i) => ({ value: String(i + 1), label }));

const emptyFilters = (uid, roleId) => ({
    Userid: uid || '',
    Roleid: roleId || '',
    CCStatusval: '',
    CCCode: '',
    DCA: '',
    DCACode: '',
    VendorCode: '',
    month: '',
    year: '',
    Status: '',
    SPPONO: ''
});

const selectCls = 'w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed';

const Field = ({ label, required, hint, children }) => (
    <div className="min-w-0">
        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        {children}
        {hint && <p className="text-[11px] text-indigo-500 dark:text-indigo-400 mt-0.5">{hint}</p>}
    </div>
);

const SPPOReportPage = () => {
    const dispatch = useDispatch();

    // Get userData from auth state
    const { userData } = useSelector((state) => state.auth);
    const roleId = userData?.roleId || userData?.RID;
    const uid = userData?.UID || userData?.uid;

    // Redux selectors
    const sppoCostCenters = useSelector(selectSPPOCostCenters);
    const sppoDCACodes = useSelector(selectSPPODCACodes);
    const sppoVendors = useSelector(selectSPPOVendors);
    const sppoReportData = useSelector(selectSPPOReportData);
    const sppoPrintData = useSelector(selectSPPOPrintData);
    const loading = useSelector(selectLoading);
    const errors = useSelector(selectErrors);
    const isAnyLoading = useSelector(selectIsAnyLoading);
    const reportSummary = useSelector(selectSPPOReportSummary);

    // Financial years from budget slice
    const allFinancialYears = useSelector(selectAllFinancialYears) || [];

    // Local state for form inputs
    const [localFilters, setLocalFilters] = useState(() => emptyFilters(uid, roleId));

    // Modal state
    const [isSPPODetailsModalOpen, setIsSPPODetailsModalOpen] = useState(false);

    // Legacy required fields: CC status, CC, DCA, vendor, PO status, year (month optional)
    const canGenerateReportLocal = () => [
        localFilters.CCStatusval,
        localFilters.CCCode,
        localFilters.DCA || localFilters.DCACode,
        localFilters.VendorCode,
        localFilters.year,
        localFilters.Status
    ].every(field => field && String(field).trim() !== '');

    // Load initial data
    useEffect(() => {
        dispatch(fetchAllFinancialYears());
    }, [dispatch]);

    // Update local filters when auth data changes
    useEffect(() => {
        if (roleId && uid) {
            setLocalFilters(prev => ({ ...prev, Roleid: roleId, Userid: uid }));
        }
    }, [roleId, uid, userData]);

    // Show error messages via toast
    useEffect(() => {
        Object.entries(errors).forEach(([key, error]) => {
            if (error && error !== null) {
                toast.error(`Error with ${key}: ${error}`);
                dispatch(clearError({ errorType: key }));
            }
        });
    }, [errors, dispatch]);

    const handleFilterChange = (filterName, value) => {
        const base = {
            ...localFilters,
            Userid: localFilters.Userid || uid || '',
            Roleid: localFilters.Roleid || roleId || ''
        };
        let newFilters;

        // Cascading dropdowns: CC status → CC → DCA → vendor
        if (filterName === 'CCStatusval' || filterName === 'CCCode') {
            dispatch(resetDependentDropdowns());
            newFilters = {
                ...base,
                ...(filterName === 'CCStatusval' ? { CCStatusval: value, CCCode: '' } : { CCCode: value }),
                DCA: '',
                DCACode: '',
                VendorCode: ''
            };
        } else if (filterName === 'DCA') {
            newFilters = { ...base, DCA: value, DCACode: value, VendorCode: '' };
            dispatch(resetVendorDropdown());
        } else {
            newFilters = { ...base, [filterName]: value };
        }

        setLocalFilters(newFilters);
        dispatch(setFilters(newFilters));
        dispatch(resetReportData());
    };

    // Load cost centers when CC status, roleId, and userid are available
    useEffect(() => {
        if (localFilters.CCStatusval && localFilters.Roleid && localFilters.Userid) {
            dispatch(fetchCCForSPPOCloseByVendor({
                Userid: localFilters.Userid,
                Roleid: localFilters.Roleid,
                CCStatusval: localFilters.CCStatusval
            }));
        }
    }, [localFilters.CCStatusval, localFilters.Roleid, localFilters.Userid, dispatch]);

    // Load DCA codes when cost center is selected (spGetDCAbyPOReport supports 'SelectAll')
    useEffect(() => {
        if (localFilters.CCCode) {
            dispatch(fetchDCAForPOReport({ CCCode: localFilters.CCCode }));
        }
    }, [localFilters.CCCode, dispatch]);

    // Load vendors when both cost center and DCA are selected
    useEffect(() => {
        if (localFilters.CCCode && localFilters.DCA) {
            dispatch(fetchVendorsForSPPOReport({ CCCode: localFilters.CCCode, DCA: localFilters.DCA }));
        }
    }, [localFilters.CCCode, localFilters.DCA, dispatch]);

    // Handle generate report
    const handleGenerateReport = async () => {
        if (!canGenerateReportLocal()) {
            toast.warning('Please fill all required fields (CC Status, Cost Center, DCA Code, Vendor, PO Status and Financial Year; Month is optional)');
            return;
        }

        try {
            dispatch(setFilters(localFilters));
            await dispatch(fetchSPPOReportData({
                CCCode: localFilters.CCCode,
                DCACode: localFilters.DCACode || localFilters.DCA,
                VendorCode: localFilters.VendorCode,
                month: localFilters.month || '',
                year: localFilters.year,
                Status: localFilters.Status,
                CCStatus: localFilters.CCStatusval
            })).unwrap();
            toast.success('SPPO report generated successfully');
        } catch (error) {
            toast.error('Failed to generate SPPO report. Please try again.');
        }
    };

    // Handle reset
    const handleReset = () => {
        setLocalFilters(emptyFilters(uid, roleId));
        dispatch(clearFilters());
        dispatch(resetReportData());
    };

    // Handle row click to view SPPO details
    const handleRowClick = async (rowData) => {
        try {
            setIsSPPODetailsModalOpen(true);
            if (rowData.SPPONo) {
                await dispatch(fetchSPPOForPrint({ SPPONO: rowData.SPPONo })).unwrap();
            }
        } catch (error) {
            toast.error('Failed to load SPPO details');
            setIsSPPODetailsModalOpen(false);
        }
    };

    // Get report data for display
    const reportData = Array.isArray(sppoReportData?.Data) ? sppoReportData.Data : [];
    const totalValueSum = reportData.reduce((s, r) => s + (parseFloat(r.TotalValue) || 0), 0);
    const balanceSum = reportData.reduce((s, r) => s + (parseFloat(r.Balance) || 0), 0);

    // Handle Excel download
    const handleExcelDownload = () => {
        try {
            if (reportData.length === 0) {
                toast.warning('No data available to download');
                return;
            }

            const excelData = reportData.map(item => ({
                'SPPO Number': item.SPPONo || '-',
                'Start Date': formatDate(item.SPPOStartDate),
                'Cost Center': item.CCCode || '-',
                'Vendor Name': item.VendorName || '-',
                'Vendor Code': item.VendorCode || '-',
                'DCA Code': item.DCACode || '-',
                'Total Value': item.TotalValue || 0,
                'Balance': item.Balance || 0,
                'PO Status': poStatusLabel(item.POStatus)
            }));

            const ccText = localFilters.CCCode === 'SelectAll' ? 'AllCCs' : localFilters.CCCode;
            const vendorText = localFilters.VendorCode === 'SelectAll' ? 'AllVendors' : localFilters.VendorCode;
            const monthText = localFilters.month ? `Month${localFilters.month}` : 'AllMonths';
            const yearText = localFilters.year === 'Any' ? 'AllYears' : localFilters.year;
            const filename = `SPPO_Report_${ccText}_${vendorText}_${localFilters.Status}_${monthText}_${yearText}_${new Date().toISOString().split('T')[0]}`;
            downloadAsExcel(excelData, filename);
            toast.success('Excel file downloaded successfully');
        } catch (error) {
            toast.error('Excel download failed. Please try again.');
        }
    };

    const thCls = 'px-3 py-2 text-[11px] font-bold text-white uppercase tracking-wide whitespace-nowrap';
    const tdCls = 'px-3 py-1.5 text-xs text-gray-900 dark:text-white whitespace-nowrap';

    return (
        <div className="space-y-3 p-4">
            {/* Header + filters */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-3">
                    <h1 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Package className="h-5 w-5 text-indigo-600" />
                        Service Provider PO Report
                    </h1>
                    {isAnyLoading && (
                        <span className="flex items-center gap-1.5 text-xs text-indigo-600 dark:text-indigo-400">
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Loading…
                        </span>
                    )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
                    <Field label="CC Status" required>
                        <select value={localFilters.CCStatusval} onChange={(e) => handleFilterChange('CCStatusval', e.target.value)} className={selectCls}>
                            <option value="">-Please Select-</option>
                            <option value="Active">Active</option>
                            <option value="Closed">Closed</option>
                        </select>
                    </Field>

                    <Field label="Cost Center" required hint={loading.sppoCostCenters ? 'Loading…' : null}>
                        <select value={localFilters.CCCode} onChange={(e) => handleFilterChange('CCCode', e.target.value)}
                            disabled={loading.sppoCostCenters || !localFilters.CCStatusval} className={selectCls}>
                            <option value="">-Please Select-</option>
                            {Array.isArray(sppoCostCenters?.Data) && sppoCostCenters.Data.length > 0 && (
                                <option value="SelectAll">Select All</option>
                            )}
                            {Array.isArray(sppoCostCenters?.Data) && sppoCostCenters.Data.map((cc, index) => {
                                const ccCode = cc?.CC_Code || cc?.costCenterCode || cc?.code;
                                const ccName = cc?.CC_Name || cc?.costCenterName || cc?.name;
                                if (!ccCode || ccCode === 'SelectAll') return null;
                                return <option key={ccCode || index} value={ccCode}>{ccName || ccCode}</option>;
                            })}
                        </select>
                    </Field>

                    <Field label="DCA Code" required hint={loading.sppoDCACodes ? 'Loading…' : null}>
                        <select value={localFilters.DCA} onChange={(e) => handleFilterChange('DCA', e.target.value)}
                            disabled={loading.sppoDCACodes || !localFilters.CCCode} className={selectCls}>
                            <option value="">-Please Select-</option>
                            {Array.isArray(sppoDCACodes?.Data) && sppoDCACodes.Data.map((dca, index) => {
                                const dcaCode = dca?.DCACode || dca?.dcaCode || dca?.code;
                                const dcaName = dca?.DCAIDSTR || dca?.dcaName || dca?.name;
                                if (!dcaCode) return null;
                                return <option key={dcaCode || index} value={dcaCode}>{dcaName || dcaCode}</option>;
                            })}
                        </select>
                    </Field>

                    <Field label="Vendor" required hint={loading.sppoVendors ? 'Loading…' : null}>
                        <select value={localFilters.VendorCode} onChange={(e) => handleFilterChange('VendorCode', e.target.value)}
                            disabled={loading.sppoVendors || !localFilters.DCA} className={selectCls}>
                            <option value="">-Please Select-</option>
                            {Array.isArray(sppoVendors?.Data) && sppoVendors.Data.length > 0 && (
                                <option value="SelectAll">Select All</option>
                            )}
                            {Array.isArray(sppoVendors?.Data) && sppoVendors.Data.map((vendor, index) => {
                                const vendorCode = vendor?.VendorCode || vendor?.vendorCode || vendor?.code;
                                const vendorName = vendor?.VendorName || vendor?.vendorName || vendor?.name;
                                if (!vendorCode) return null;
                                return <option key={vendorCode || index} value={vendorCode}>{vendorName || vendorCode}</option>;
                            })}
                        </select>
                    </Field>

                    <Field label="PO Status" required>
                        <select value={localFilters.Status} onChange={(e) => handleFilterChange('Status', e.target.value)} className={selectCls}>
                            <option value="">-Please Select-</option>
                            {PO_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </Field>

                    <Field label="Month">
                        <select value={localFilters.month} onChange={(e) => handleFilterChange('month', e.target.value)} className={selectCls}>
                            <option value="">All Months</option>
                            {MONTH_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </Field>

                    <Field label="Financial Year" required>
                        <select value={localFilters.year} onChange={(e) => handleFilterChange('year', e.target.value)} className={selectCls}>
                            <option value="">-Please Select-</option>
                            <option value="Any">Select All</option>
                            {Array.isArray(allFinancialYears?.Data) && allFinancialYears.Data.map((year, index) => {
                                const yearValue = year?.Year || year?.financialYear || year?.year;
                                const yearName = year?.YearValue || year?.YearName || year?.yearName || yearValue;
                                if (!yearValue || yearValue === 'Any') return null;
                                return <option key={yearValue || index} value={yearValue}>{yearName}</option>;
                            })}
                        </select>
                    </Field>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between gap-3 mt-3">
                    <div className="flex gap-2">
                        <button onClick={handleGenerateReport} disabled={isAnyLoading || !canGenerateReportLocal()}
                            className="px-4 py-1.5 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5">
                            {loading.sppoReportData ? <RotateCcw className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                            View Report
                        </button>
                        <button onClick={handleReset}
                            className="px-4 py-1.5 text-sm bg-gray-600 hover:bg-gray-700 text-white rounded-md flex items-center gap-1.5">
                            <RotateCcw className="h-4 w-4" /> Reset
                        </button>
                    </div>
                    <Tooltip content="Download SPPO report as Excel file">
                        <button onClick={handleExcelDownload} disabled={reportData.length === 0}
                            className="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5">
                            <Download className="h-4 w-4" /> Excel
                        </button>
                    </Tooltip>
                </div>
            </div>

            {/* Summary */}
            {reportData.length > 0 && <SummaryCards reportSummary={reportSummary} />}

            {/* Report Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                {reportData.length > 0 ? (
                    <div className="overflow-auto max-h-[65vh]">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-indigo-600 sticky top-0 z-10">
                                <tr>
                                    <th className={`${thCls} text-left`}>#</th>
                                    <th className={`${thCls} text-left`}>SPPO No.</th>
                                    <th className={`${thCls} text-left`}>Start Date</th>
                                    <th className={`${thCls} text-left`}>Cost Center</th>
                                    <th className={`${thCls} text-left`}>Vendor</th>
                                    <th className={`${thCls} text-left`}>DCA</th>
                                    <th className={`${thCls} text-right`}>Total</th>
                                    <th className={`${thCls} text-right`}>Balance</th>
                                    <th className={`${thCls} text-center`}>PO Status</th>
                                    <th className={`${thCls} text-center`}>View</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {reportData.map((item, index) => (
                                    <tr key={item.SPPOId || index} className="hover:bg-indigo-50/50 dark:hover:bg-gray-700/60">
                                        <td className={`${tdCls} text-gray-500 dark:text-gray-400`}>{index + 1}</td>
                                        <td className={`${tdCls} font-medium`}>{item.SPPONo || '-'}</td>
                                        <td className={tdCls}>{formatDate(item.SPPOStartDate)}</td>
                                        <td className={tdCls}>{item.CCCode || '-'}</td>
                                        <td className="px-3 py-1.5 text-xs text-gray-900 dark:text-white">
                                            <div className="max-w-[220px] truncate" title={item.VendorName}>{item.VendorName || '-'}</div>
                                        </td>
                                        <td className={tdCls}>{item.DCACode || '-'}</td>
                                        <td className={`${tdCls} text-right font-semibold tabular-nums`}>₹{formatCurrency(item.TotalValue || 0)}</td>
                                        <td className={`${tdCls} text-right font-semibold tabular-nums text-indigo-600 dark:text-indigo-400`}>₹{formatCurrency(item.Balance || 0)}</td>
                                        <td className={`${tdCls} text-center`}>
                                            <span className={clsx(
                                                'px-1.5 py-0.5 rounded text-[10px] font-semibold',
                                                item.POStatus === 'Open'
                                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
                                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                            )}>
                                                {poStatusLabel(item.POStatus)}
                                            </span>
                                        </td>
                                        <td className={`${tdCls} text-center`}>
                                            <button onClick={() => handleRowClick(item)} title="View SPPO"
                                                className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300">
                                                <Eye className="h-4 w-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-gray-50 dark:bg-gray-700/60 sticky bottom-0">
                                <tr>
                                    <td colSpan={6} className="px-3 py-2 text-xs font-bold text-right text-gray-700 dark:text-gray-200">Total</td>
                                    <td className="px-3 py-2 text-xs font-bold text-right tabular-nums text-gray-900 dark:text-white">₹{formatCurrency(totalValueSum)}</td>
                                    <td className="px-3 py-2 text-xs font-bold text-right tabular-nums text-indigo-600 dark:text-indigo-400">₹{formatCurrency(balanceSum)}</td>
                                    <td colSpan={2} />
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                ) : loading.sppoReportData ? (
                    <div className="p-6 flex items-center justify-center gap-2 text-sm text-indigo-600 dark:text-indigo-400">
                        <RotateCcw className="h-4 w-4 animate-spin" /> Fetching SPPO data…
                    </div>
                ) : (
                    <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                        Select the filters and click <strong>View Report</strong> to see SPPO data.
                    </div>
                )}
            </div>

            {/* SPPO Details Modal */}
            <SPPODetailsModal
                isOpen={isSPPODetailsModalOpen}
                onClose={() => setIsSPPODetailsModalOpen(false)}
                sppoData={sppoPrintData}
                loading={loading.sppoPrintData}
            />
        </div>
    );
};

export default SPPOReportPage;
